import React, { useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RepeatIcon from '@mui/icons-material/Repeat';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Loading from '../components/loading';
import SuccessMessage from '../components/successMessage';
import ErrorMessage from '../components/errorMessage';
import KanbanTaskCard from './kanbanTaskCard';
import KanbanTaskCardModal from './kanbanTaskCardModal';
import KanbanTaskAnnotationModal from './kanbanTaskAnnotationModal';
import KanbanTaskRecurringModal from './kanbanTaskRecurringModal';
import { columns } from './kanbanTaskConstants';
import useTaskBoard from './useTaskBoard';

export default function KanbanTaskMobile() {
  const {
    tasksByColumn,
    isLoading,
    isToday,
    shortFormattedDate,
    goToPreviousDay,
    goToNextDay,
    goToToday,
    taskModalOpen,
    editingTask,
    openNewTaskModal,
    openEditTaskModal,
    closeTaskModal,
    handleSaveTask,
    recurringTasks,
    recurringModalOpen,
    setRecurringModalOpen,
    handleRecurringAdd,
    handleRecurringUpdate,
    handleRecurringDelete,
    handleRecurringMove,
    handleAdvanceStage,
    handlePreviousStage,
    deleteModal,
    handleDeleteClick,
    closeDeleteModal,
    handleConfirmDelete,
    notesModalOpen,
    notesTask,
    openNotesModal,
    closeNotesModal,
    handleAddNote,
    showSuccessMessage,
    successMessage,
    closeSuccessMessage,
    showErrorMessage,
    errorMessage,
    closeErrorMessage,
  } = useTaskBoard();

  // O quadro de 3 colunas não cabe na tela do celular: cada etapa vira uma aba
  const [activeColumnIndex, setActiveColumnIndex] = useState(0);

  const activeColumn = columns[activeColumnIndex];
  const activeColumnTasks = tasksByColumn[activeColumnIndex];

  return (
    <Box>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={closeSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage message={errorMessage} show={showErrorMessage} setShow={closeErrorMessage} />
      )}

      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 1 }}>
        Tarefas
      </Typography>

      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        justifyContent="center"
        sx={{ mb: 1 }}
      >
        <IconButton size="small" onClick={goToPreviousDay} aria-label="Dia anterior">
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ minWidth: 130, textAlign: 'center', textTransform: 'capitalize' }}
        >
          {shortFormattedDate}
        </Typography>
        <IconButton size="small" onClick={goToNextDay} aria-label="Próximo dia">
          <ChevronRightIcon fontSize="small" />
        </IconButton>
        {!isToday && (
          <Button size="small" onClick={goToToday}>
            Hoje
          </Button>
        )}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openNewTaskModal}
          sx={{ flex: 1 }}
        >
          Nova tarefa
        </Button>
        <Button
          variant="outlined"
          onClick={() => setRecurringModalOpen(true)}
          aria-label="Tarefas recorrentes"
          sx={{ flexShrink: 0, minWidth: 0, px: 1.5 }}
        >
          <RepeatIcon fontSize="small" />
        </Button>
      </Stack>

      <Tabs
        value={activeColumnIndex}
        onChange={(event, value) => setActiveColumnIndex(value)}
        variant="fullWidth"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 2,
          minHeight: 44,
          '& .MuiTabs-indicator': { backgroundColor: activeColumn.color },
        }}
      >
        {columns.map((column, columnIndex) => (
          <Tab
            key={column.id}
            sx={{ minHeight: 44, px: 1 }}
            label={
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {column.title}
                </Typography>
                <Chip
                  label={tasksByColumn[columnIndex].length}
                  size="small"
                  sx={{
                    backgroundColor: column.color,
                    color: '#fff',
                    fontWeight: 'bold',
                    height: 18,
                    '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
                  }}
                />
              </Stack>
            }
          />
        ))}
      </Tabs>

      <Stack spacing={1.5}>
        {activeColumnTasks.length === 0 ? (
          <Box sx={{ textAlign: 'center', color: 'text.disabled', fontSize: '0.85rem', py: 4 }}>
            Nenhuma tarefa
          </Box>
        ) : (
          activeColumnTasks.map((task) => (
            <KanbanTaskCard
              key={task.id}
              task={task}
              isFirstColumn={activeColumnIndex === 0}
              isLastColumn={activeColumnIndex === columns.length - 1}
              onEdit={openEditTaskModal}
              onDelete={handleDeleteClick}
              onOpenNotes={openNotesModal}
              onAdvance={handleAdvanceStage}
              onPrevious={handlePreviousStage}
            />
          ))
        )}
      </Stack>

      <KanbanTaskCardModal
        open={taskModalOpen}
        task={editingTask}
        onClose={closeTaskModal}
        onSave={handleSaveTask}
      />

      <KanbanTaskRecurringModal
        open={recurringModalOpen}
        onClose={() => setRecurringModalOpen(false)}
        recurringTasks={recurringTasks}
        onAdd={handleRecurringAdd}
        onUpdate={handleRecurringUpdate}
        onDelete={handleRecurringDelete}
        onMove={handleRecurringMove}
      />

      <KanbanTaskAnnotationModal
        open={notesModalOpen}
        task={notesTask}
        onClose={closeNotesModal}
        onAddNote={handleAddNote}
      />

      <Dialog open={deleteModal} onClose={closeDeleteModal} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir tarefa</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            Esta ação remove a tarefa e suas anotações permanentemente. Deseja continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteModal}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
